import React, { type ReactElement, useMemo, useState } from 'react';
import { useForm as useFormViewer } from '@wix/form-viewer';
import { type Layout, type InputElement } from '@wix/form-fields';

import { type UseFormProps, type UploadFile } from '../types/types';
import { normalizeSchema } from '../utils/normalize-schema';
import { FIELD_TYPE_MAP } from '../mappers/field-type-map';
import { mapFieldTypes } from '../utils/mappers';

type UseFormReturn = {
  columnCount: number;
  fieldElements: ReactElement[][];
  fieldsLayout: Record<string, Layout>;
} | null;

type Actions = { submitForm?: () => void; uploadFile?: UploadFile };

export const useForm = ({
  form: unformattedForm,
  values,
  onChange,
  errors,
  onValidate,
  submitForm,
  uploadFile,
  forwardedRef,
  fieldMap: fields,
  addressTemplates,
}: UseFormProps): UseFormReturn => {
  const form = normalizeSchema(unformattedForm);
  const [stepId, setStepId] = useState<string>();

  const actions: Actions = {
    submitForm,
    uploadFile,
  };

  const mappedFieldTypes = useMemo(
    () => mapFieldTypes(fields, FIELD_TYPE_MAP),
    [fields],
  );

  const { currentView, formFields } = useFormViewer({
    form,
    actions,
    values,
    stepId,
    onStepIdChange: setStepId,
    onChange,
    forwardedRef,
    errors,
    onValidate,
    fieldMap: mappedFieldTypes,
    config: {
      addressTemplates,
    },
  });

  const fieldsLayout = currentView.fields.reduce<Record<string, Layout>>(
    (acc, field) => {
      acc[field.id] = field.layout;
      return acc;
    },
    {},
  );

  const fieldElementsByRow = useMemo(() => {
    const fieldsByRow = new Map<number, ReactElement[]>();

    formFields.forEach(({ component }: InputElement, index: number) => {
      const field = currentView.fields[index];
      const row = field?.layout.row ?? 0;

      if (!fieldsByRow.has(row)) {
        fieldsByRow.set(row, []);
      }

      fieldsByRow
        .get(row)!
        .push(React.cloneElement(component, { key: field.id }));
    });

    return Array.from(fieldsByRow.entries())
      .sort(([rowA], [rowB]) => rowA - rowB)
      .map(([_, elements]) => elements);
  }, [formFields, currentView.fields]);

  return form
    ? {
        columnCount: currentView.grid.columns,
        fieldElements: fieldElementsByRow,
        fieldsLayout,
      }
    : null;
};
